import { useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, CSSProperties, ReactNode } from 'react'
import { useOverlayStore } from '../store/overlay.store'
import { TourProvider, useTour } from '@reactour/tour'
import { Palette } from 'src/feature/theme/model/Palette'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useTourSteps } from 'src/hook/useTourSteps'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'

// Tour Auto Start on first visit
const AutoStartTour = observer(() => {
  const { setIsOpen, steps } = useTour()
  const overlayStore = useOverlayStore()

  useEffect(() => {
    if (!steps || steps.length === 0) {
      return
    }

    // Show tour if not seen before
    // Handle case when store is not initialized (cache cleared)
    const hasSeenTour = overlayStore?.tourTipsShown === true
    
    if (!hasSeenTour) {
      setIsOpen(true)
    }
  }, [setIsOpen, steps, overlayStore])

  return null
})

// Main component with Tour provider and Auto start
export const GlobalOverlay = observer(({ children }: { children: ReactNode }) => {
  const theme = useTheme()
  const { activeTourSteps } = useTourSteps()
  const overlayStore = useOverlayStore()
  const { locale } = useLocalizationStore()

  // Mark tour as seen when closed
  const handleClose = () => {
    overlayStore.markTourAsSeen()
  }

  useEffect(() => {
    if (activeTourSteps.length === 0) {
      return
    }

    const style = document.createElement('style')
    style.textContent = TOUR_BUTTON_CSS
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [activeTourSteps.length])

  // Only render TourProvider when we have steps, if else render children
  if (activeTourSteps.length === 0) {
    return <>{children}</>
  }
  
  return (
    <TourProvider
      key={locale}
      steps={activeTourSteps as any}
      styles={getTourStyles(theme)}
      beforeClose={handleClose}
    >
      {children}
      <AutoStartTour />
      {/* Invisible anchor for centered tour tips */}
      <AppBox className="contactCard" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
    </TourProvider>
  )
})

// Tour button styles
// Global CSS for tour close button
const TOUR_BUTTON_CSS = `
  .reactour__close-button svg {
    pointer-events: none !important;
    font-size: medium !important;
    width: 0.8em !important;
    height: 0.8em !important;
  }
  .reactour__close-button:hover {
    transform: scale(1.05) !important;
    border-color: #27827A !important;
    background-color: #27827A30 !important;
  }
`

// Style for the tour close button
const closeButtonStyle: CSSProperties = {
  backgroundColor: `${Palette.CORAL_REEF_GREEN}30`,
  color: Palette.CORAL_REEF_GREEN,
  border: `2px solid ${Palette.CORAL_REEF_GREEN}30`,
  borderRadius: '50%',
  width: '1.2rem',
  height: '1.2rem',
  transition: 'transform 0.3s ease, border-color 0.3s ease',
  cursor: 'pointer',
  position: 'absolute',
  top: '12px',
  right: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

// Custom styles for tour components
const getTourStyles = (theme: any) => ({
  popover: (base: CSSProperties) => ({
    ...base,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    marginLeft: '20px',
    borderRadius: '10px',
    padding: '24px 40px 24px 24px', // Extra right padding for close button
  }),
  badge: (base: CSSProperties) => ({
    ...base,
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
  }),
  dot: (base: CSSProperties, state: any) => ({
    ...base,
    backgroundColor: state.current ? theme.palette.primary.main : base.backgroundColor,
  }),
  close: () => closeButtonStyle,
  arrow: (base: CSSProperties, state: any) => ({
    ...base,
    color: state.disabled ? theme.palette.text.secondary : theme.palette.text.primary,
  }),
})